import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useHouseholdMembers } from '../hooks/useHousehold';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import {
  useChoreDefinitions,
  useCreateChoreDefinition,
  useUpdateChoreDefinition,
  useDeleteChoreDefinition,
} from '../hooks/useChoreDefinitions';
import { useRooms } from '../hooks/useRooms';
import { ChoreLength, ChorePriority, Recurrence, type ChoreDefinition } from '@chore-project/shared';
import { Modal } from '../components/Modal';
import { MemberAvatar } from '../components/MemberAvatar';
import { EmptyState, NoHouseholdEmpty } from '../components/EmptyState';
import { HouseholdSwitcher } from '../components/HouseholdSwitcher';
import { PRIORITY_META, LENGTH_META } from '../lib/chore-meta';
import { calcPoints } from '../lib/dashboard';

const DAY_ABBREV = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const PRIORITY_CHIP = [ChorePriority.MUST_DO, ChorePriority.SHOULD_DO, ChorePriority.CAN_DO] as const;
const LENGTH_CHIP = [ChoreLength.SHORT, ChoreLength.MEDIUM, ChoreLength.LONG] as const;

const RECURRENCE_CHIP = [
  { value: Recurrence.NONE,    label: 'None',    icon: 'remove-circle-outline' },
  { value: Recurrence.DAILY,   label: 'Daily',   icon: 'today-outline' },
  { value: Recurrence.WEEKLY,  label: 'Weekly',  icon: 'calendar-outline' },
  { value: Recurrence.MONTHLY, label: 'Monthly', icon: 'calendar-number-outline' },
] as const;

function recurrenceLabel(recurrence: Recurrence, daysOfWeek?: number[], daysOfMonth?: number[]): string {
  switch (recurrence) {
    case Recurrence.DAILY:   return 'Daily';
    case Recurrence.WEEKLY: {
      if (!daysOfWeek?.length) return 'Weekly';
      return `Weekly · ${daysOfWeek.map((d) => DAY_ABBREV[d] ?? d).join(', ')}`;
    }
    case Recurrence.MONTHLY: {
      if (!daysOfMonth?.length) return 'Monthly';
      return `Monthly · ${daysOfMonth.join(', ')}`;
    }
    default: return 'No recurrence';
  }
}

function groupByRoom(
  defs: ChoreDefinition[],
  roomMap: Map<string, string>,
): Array<{ roomLabel: string; items: ChoreDefinition[] }> {
  const groups = new Map<string, ChoreDefinition[]>();
  for (const def of defs) {
    const key = def.roomId && roomMap.has(def.roomId) ? def.roomId : '';
    const group = groups.get(key) ?? [];
    group.push(def);
    groups.set(key, group);
  }
  const result: Array<{ roomLabel: string; items: ChoreDefinition[] }> = [];
  const namedKeys = [...groups.keys()]
    .filter((k) => k !== '')
    .sort((a, b) => (roomMap.get(a) ?? '').localeCompare(roomMap.get(b) ?? ''));
  for (const key of namedKeys) {
    result.push({ roomLabel: roomMap.get(key)!, items: groups.get(key)! });
  }
  if (groups.has('')) {
    result.push({ roomLabel: 'Other', items: groups.get('')! });
  }
  return result;
}

export function ChoreDefinitionsPage() {
  const { householdId } = useSelectedHousehold();
  const { data: members } = useHouseholdMembers(householdId);
  const { data: defs = [], isLoading } = useChoreDefinitions(householdId);
  const { data: rooms = [] } = useRooms(householdId);
  const createDef = useCreateChoreDefinition(householdId);
  const updateDef = useUpdateChoreDefinition(householdId);
  const deleteDef = useDeleteChoreDefinition(householdId);

  const roomMap = new Map(rooms.map((r) => [r.roomId, r.name]));

  const [showModal, setShowModal] = useState(false);
  const [editingDef, setEditingDef] = useState<ChoreDefinition | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] = useState<Recurrence>(Recurrence.NONE);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]);
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>([1]);
  const [defaultAssigneeId, setDefaultAssigneeId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [length, setLength] = useState<ChoreLength>(ChoreLength.MEDIUM);
  const [priority, setPriority] = useState<ChorePriority>(ChorePriority.SHOULD_DO);

  const openCreate = () => {
    setEditingDef(null);
    setName('');
    setDescription('');
    setRecurrence(Recurrence.NONE);
    setDaysOfWeek([1]);
    setDaysOfMonth([1]);
    setDefaultAssigneeId('');
    setRoomId('');
    setLength(ChoreLength.MEDIUM);
    setPriority(ChorePriority.SHOULD_DO);
    setShowModal(true);
  };

  const openEdit = (def: ChoreDefinition) => {
    setEditingDef(def);
    setName(def.name);
    setDescription(def.description ?? '');
    setRecurrence(def.recurrence);
    setDaysOfWeek(def.daysOfWeek ?? [1]);
    setDaysOfMonth(def.daysOfMonth ?? [1]);
    setDefaultAssigneeId(def.defaultAssigneeId ?? '');
    setRoomId(def.roomId ?? '');
    setLength(def.length ?? ChoreLength.MEDIUM);
    setPriority(def.priority ?? ChorePriority.SHOULD_DO);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDef(null);
  };

  // Open the create modal when arriving from the quick-add (+) sheet.
  const location = useLocation();
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      openCreate();
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      description: description || undefined,
      recurrence,
      daysOfWeek: recurrence === Recurrence.WEEKLY ? daysOfWeek : undefined,
      daysOfMonth: recurrence === Recurrence.MONTHLY ? daysOfMonth : undefined,
      defaultAssigneeId: defaultAssigneeId || undefined,
      roomId: roomId || undefined,
      length,
      priority,
    };
    if (editingDef) {
      await updateDef.mutateAsync({ choreDefId: editingDef.choreDefId, ...payload });
    } else {
      await createDef.mutateAsync(payload);
    }
    closeModal();
  };

  const isPending = createDef.isPending || updateDef.isPending;

  if (!householdId) {
    return (
      <div className="page">
        <NoHouseholdEmpty />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <p className="loading-note">Loading your chore library…</p>
      </div>
    );
  }

  const groups = rooms.length > 0 ? groupByRoom(defs, roomMap) : null;

  const renderDef = (def: ChoreDefinition) => {
    const assignee = def.defaultAssigneeId
      ? (members ?? []).find((m) => m.userId === def.defaultAssigneeId)
      : undefined;
    const priority = def.priority ?? ChorePriority.SHOULD_DO;
    const length = def.length ?? ChoreLength.MEDIUM;
    const pMeta = PRIORITY_META[priority];
    const pts = calcPoints(priority, length);
    return (
      <button
        key={def.choreDefId}
        className="chore-card chore-card-button"
        onClick={() => openEdit(def)}
        aria-label={`Edit ${def.name}`}
      >
        <div className="chore-row">
          <div className="chore-card-body">
            <span className="chore-title">{def.name}</span>
            {def.description && <span className="chore-desc">{def.description}</span>}
            <span className="chore-meta">
              <span className="chore-meta-item" style={{ color: pMeta.color }}>
                <ion-icon name={pMeta.icon} />
                {pMeta.label} do
              </span>
              <span className="chore-meta-item">
                <ion-icon name="repeat-outline" />
                {recurrenceLabel(def.recurrence, def.daysOfWeek, def.daysOfMonth)}
              </span>
              <span className="chore-meta-item">
                <ion-icon name="timer-outline" />
                {LENGTH_META[length].label}
              </span>
              {assignee && (
                <span className="chore-meta-item">
                  <MemberAvatar member={assignee} size="1.1rem" fontSize="0.55rem" />
                  {assignee.memberName || assignee.email}
                </span>
              )}
            </span>
          </div>
          <div className="chore-trailing chore-trailing--stack">
            <span className="pts-chip" title={`Worth ${pts} points per completion`}>+{pts}</span>
            <ion-icon name="chevron-forward" aria-hidden="true" className="chore-trailing-chevron" />
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Chore library</h1>
        <div className="page-header-actions">
          <HouseholdSwitcher />
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            + New chore
          </button>
        </div>
      </div>

      {groups ? (
        groups.length === 0 ? (
          <EmptyState
            icon="sparkles-outline"
            title="Build your chore library"
            action={<button className="btn btn-primary" onClick={openCreate}>Add your first chore</button>}
          >
            Recurring chores live here — add one and Chortle schedules it for you.
          </EmptyState>
        ) : (
          groups.map(({ roomLabel, items }) => (
            <section key={roomLabel} className="chore-stack">
              <p className="section-label">{roomLabel} — {items.length}</p>
              {items.map(renderDef)}
            </section>
          ))
        )
      ) : (
        <section>
          <p className="section-label">Your chores — {defs.length}</p>
          {defs.length === 0 && (
            <EmptyState
              icon="sparkles-outline"
              title="Build your chore library"
              action={<button className="btn btn-primary" onClick={openCreate}>Add your first chore</button>}
            >
              Recurring chores live here — add one and Chortle schedules it for you.
            </EmptyState>
          )}
          {defs.length > 0 && <div className="chore-stack">{defs.map(renderDef)}</div>}
        </section>
      )}

      {showModal && (
        <Modal title={editingDef ? 'Edit chore' : 'New chore'} onClose={closeModal}>
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className="field">
              <label htmlFor="chore-name">Name</label>
              <input
                id="chore-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Vacuum living room"
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label htmlFor="chore-desc">Description (optional)</label>
              <input
                id="chore-desc"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Any additional details…"
              />
            </div>
            {rooms.length > 0 && (
              <div className="field">
                <label htmlFor="chore-room">Room (optional)</label>
                <select
                  id="chore-room"
                  className="input-inline"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                >
                  <option value="">— no room —</option>
                  {rooms.map((r) => (
                    <option key={r.roomId} value={r.roomId}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label>Priority</label>
              <div className="chip-row">
                {PRIORITY_CHIP.map((value) => {
                  const { icon, color, label } = PRIORITY_META[value];
                  const active = priority === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`choice-chip${active ? ' choice-chip-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => setPriority(value)}
                      style={active ? { borderColor: color, color, background: `color-mix(in oklch, ${color} 12%, transparent)` } : undefined}
                    >
                      <ion-icon name={icon} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="field">
              <label>Length</label>
              <div className="chip-row">
                {LENGTH_CHIP.map((value) => {
                  const active = length === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`choice-chip${active ? ' choice-chip-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => setLength(value)}
                    >
                      <ion-icon name="timer-outline" />
                      {LENGTH_META[value].label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="field">
              <label>Recurrence</label>
              <div className="chip-row">
                {RECURRENCE_CHIP.map(({ value, label, icon }) => {
                  const active = recurrence === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`choice-chip${active ? ' choice-chip-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => setRecurrence(value)}
                    >
                      <ion-icon name={icon} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {recurrence === Recurrence.WEEKLY && (
              <div className="field">
                <label>Days of week</label>
                <div className="chip-row">
                  {DAY_ABBREV.map((abbrev, i) => {
                    const active = daysOfWeek.includes(i);
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`choice-chip choice-chip-solid choice-chip--day${active ? ' choice-chip-active' : ''}`}
                        aria-pressed={active}
                        onClick={() =>
                          setDaysOfWeek((prev) =>
                            active ? prev.filter((d) => d !== i) : [...prev, i].sort((a, b) => a - b),
                          )
                        }
                      >
                        {abbrev}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {recurrence === Recurrence.MONTHLY && (
              <div className="field">
                <label>Days of month</label>
                <div className="chip-grid">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                    const active = daysOfMonth.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        className={`choice-chip choice-chip-solid${active ? ' choice-chip-active' : ''}`}
                        aria-pressed={active}
                        onClick={() =>
                          setDaysOfMonth((prev) =>
                            active ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
                          )
                        }
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="field">
              <label>Assignee (optional)</label>
              {(members ?? []).length > 5 ? (
                <select
                  className="input-inline"
                  value={defaultAssigneeId}
                  onChange={(e) => setDefaultAssigneeId(e.target.value)}
                >
                  <option value="">— random —</option>
                  {(members ?? []).map((m) => (
                    <option key={m.userId} value={m.userId}>{m.memberName || m.email}</option>
                  ))}
                </select>
              ) : (
                <div className="chip-row">
                  <button
                    type="button"
                    className={`choice-chip${defaultAssigneeId === '' ? ' choice-chip-active' : ''}`}
                    aria-pressed={defaultAssigneeId === ''}
                    onClick={() => setDefaultAssigneeId('')}
                  >
                    <ion-icon name="shuffle-outline" />
                    Random
                  </button>
                  {(members ?? []).map((m) => {
                    const active = defaultAssigneeId === m.userId;
                    return (
                      <button
                        key={m.userId}
                        type="button"
                        className={`choice-chip${active ? ' choice-chip-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => setDefaultAssigneeId(m.userId)}
                      >
                        <MemberAvatar member={m} size="1.25rem" fontSize="0.6rem" />
                        {m.memberName || m.email?.split('@')[0] || m.email || 'Housemate'}
                      </button>
                    );
                  })}
                </div>
              )}
              {defaultAssigneeId === '' && (
                <p className="field-hint">
                  Chores will be assigned automatically from household members.
                </p>
              )}
            </div>
            <div className="form-actions">
              <button type="submit" disabled={isPending} className="btn btn-primary btn-full">
                {isPending ? 'Saving…' : editingDef ? 'Save changes' : 'Add chore'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
            </div>
            {editingDef && (
              <button
                type="button"
                className="btn btn-danger btn-full"
                style={{ marginTop: '0.6rem' }}
                disabled={deleteDef.isPending}
                onClick={() => {
                  if (!window.confirm(`Delete "${editingDef.name}"? Future scheduling stops; this can't be undone.`)) return;
                  deleteDef.mutate(editingDef.choreDefId);
                  closeModal();
                }}
              >
                <ion-icon name="trash-outline" style={{ fontSize: '1rem' }} />
                {deleteDef.isPending ? 'Deleting…' : 'Delete this chore'}
              </button>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
